import { ICommonListModel } from "api/services/types";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  HrCapitalFormationDistributionHistoryGetByEmployeeDocument,
  HrCapitalFormationDistributionHistoryPaginatedDocument,
  HrCapitalFormationDistributionHistoryCreateDocument,
  HrCapitalFormationDistributionHistoryUpdateDocument,
  HrCapitalFormationDistributionHistoryDeleteDocument,
  HrCapitalFormationDistributionCurrencyListDocument,
  HrCapitalFormationDistributionHistoryGetCapitalTrackingDocument,
} from "api/generated";
import { Optional } from "config";

import {
  IHrCapitalFormationDistributionHistoryModel,
  IHrCapitalFormationDistributionHistoryCreateVars,
  IHrCapitalFormationDistributionHistoryPaginatedResponse,
  IHrCapitalFormationDistributionHistoryPaginatedVars,
  IHrCapitalFormationDistributionHistoryUpdateVars,
  IHrCapitalFormationDistributionHistoryCapitalTrackingModel,
} from "./types";

export async function getHrCapitalFormationDistributionCurrencyList(): Promise<
  ICommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrCapitalFormationDistributionCurrencyListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrCapitalFormationDistributionCurrencyList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrCapitalFormationDistributionHistory(
  employeeId: number
): Promise<Optional<IHrCapitalFormationDistributionHistoryModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrCapitalFormationDistributionHistoryGetByEmployeeDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrCapitalFormationDistributionHistoryGetByEmployee;
  } catch (error) {
    throw error;
  }
}

export async function getHrCapitalFormationDistributionHistoryCapitalTracking(
  employeeId: number,
  date?: Date
): Promise<
  Optional<IHrCapitalFormationDistributionHistoryCapitalTrackingModel>
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrCapitalFormationDistributionHistoryGetCapitalTrackingDocument,
      variables: {
        employeeId,
        date,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrCapitalFormationDistributionHistoryGetCapitalTracking;
  } catch (error) {
    throw error;
  }
}

export async function getHrCapitalFormationDistributionHistoryPaginated(
  variables: IHrCapitalFormationDistributionHistoryPaginatedVars
): Promise<Optional<IHrCapitalFormationDistributionHistoryPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrCapitalFormationDistributionHistoryPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrCapitalFormationDistributionHistoryListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createHrCapitalFormationDistributionHistory(
  variables: IHrCapitalFormationDistributionHistoryCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrCapitalFormationDistributionHistoryCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all distribution history lists so they refetch with the new item
    executeEviction(client, {
      typename: "HrCapitalFormationDistributionHistory",
      fieldNames: [
        "hrCapitalFormationDistributionHistoryListPaginated",
        "hrCapitalFormationDistributionHistoryGetByEmployee",
        "hrCapitalFormationDistributionHistoryGetCapitalTracking",
      ],
    });

    return Boolean(res?.data?.hrCapitalFormationDistributionHistoryCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateHrCapitalFormationDistributionHistory(
  variables: IHrCapitalFormationDistributionHistoryUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrCapitalFormationDistributionHistoryUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "HrCapitalFormationDistributionHistory",
      id: variables.id?.toString(),
      fieldNames: [
        "hrCapitalFormationDistributionHistoryListPaginated",
        "hrCapitalFormationDistributionHistoryGetByEmployee",
        "hrCapitalFormationDistributionHistoryGetCapitalTracking",
      ],
    });

    return Boolean(res?.data?.hrCapitalFormationDistributionHistoryUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteHrCapitalFormationDistributionHistory(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrCapitalFormationDistributionHistoryDeleteDocument,
      variables: { id },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "HrCapitalFormationDistributionHistory",
      id: id.toString(),
      fieldNames: [
        "hrCapitalFormationDistributionHistoryListPaginated",
        "hrCapitalFormationDistributionHistoryGetByEmployee",
        "hrCapitalFormationDistributionHistoryGetCapitalTracking",
      ],
    });

    return Boolean(res?.data?.hrCapitalFormationDistributionHistoryDelete);
  } catch (error) {
    throw error;
  }
}
